import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Trash2, RefreshCw, AlertCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { apiClient } from '../../api/client';
import { config } from '../../utils/config';
import InvoicesSubNav from '../InvoicesSubNav';

interface Zone {
  id: string;
  name: string;
  type: string;
  coordinates: { x: number; y: number; width: number; height: number };
  required: boolean;
}

interface Template {
  id: string;
  name: string;
  description: string;
  lastUsed: string;
  createdAt: string;
  hasPdf: boolean;
  zones: Zone[];
}

const LIST_PATH = '/uploaded-invoices/templates';

const TemplateViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<Template | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (id) fetchTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (template?.hasPdf && id) fetchTemplatePdf();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, id]);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`${config.endpoints.templates}/${id}`);
      setTemplate(response.data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not load this template');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplatePdf = async () => {
    if (!id) return;
    try {
      const response = await apiClient.get(`${config.endpoints.templates}/${id}/pdf`, { responseType: 'blob' });
      if (response.data) {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        setPdfUrl(URL.createObjectURL(blob));
      }
    } catch {
      // PDF is optional; ignore load failures
    }
  };

  const handleReplace = () => navigate(`${LIST_PATH}/new`, { state: { replaceId: id } });

  const handleDelete = async () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      setTimeout(() => setDeleteConfirm(false), 3000);
      return;
    }
    try {
      setDeleting(true);
      await apiClient.delete(`${config.endpoints.templates}/${id}`);
      navigate(LIST_PATH);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not delete this template');
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-coral-500/20 border-t-coral-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="container mx-auto px-4 py-8">
        <InvoicesSubNav />
        <div className="flex flex-col items-center justify-center py-16">
          <p className="text-red-300 mb-4">{error || 'Template not found'}</p>
          <button
            onClick={() => navigate(LIST_PATH)}
            className="flex items-center gap-2 px-4 py-2 bg-coral-500/20 rounded-lg text-foreground-muted"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to templates
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <InvoicesSubNav />

      <div className="mt-6">
        <button
          onClick={() => navigate(LIST_PATH)}
          className="flex items-center gap-2 text-foreground-muted hover:text-foreground-secondary mb-4 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to templates
        </button>

        {/* Header */}
        <div className="flex items-start justify-between mb-6 gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-white truncate">{template.name}</h1>
            <p className="text-sm text-foreground-muted">
              {template.zones.length} {template.zones.length === 1 ? 'detail' : 'details'} learned from a sample invoice
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleReplace}
              className="flex items-center gap-2 px-4 py-2 bg-coral-500/20 hover:bg-coral-500/30 rounded-lg text-foreground-muted"
            >
              <RefreshCw className="w-4 h-4" />
              Replace sample
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                deleteConfirm
                  ? 'bg-red-500/30 text-red-200'
                  : 'bg-red-500/10 hover:bg-red-500/20 text-red-300'
              }`}
            >
              <Trash2 className="w-4 h-4" />
              {deleteConfirm ? 'Click to confirm' : 'Delete'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Details we learned */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Details we look for</CardTitle>
            </CardHeader>
            <CardContent>
              {template.zones.length > 0 ? (
                <ul className="space-y-2">
                  {template.zones.map((zone) => (
                    <li key={zone.id} className="flex items-center gap-3 p-3 rounded-lg bg-coral-500/5">
                      <FileText className="w-4 h-4 text-coral-400 flex-shrink-0" />
                      <span className="text-sm text-foreground">{zone.name}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-foreground-muted text-sm">No details saved for this template.</p>
              )}
            </CardContent>
          </Card>

          {/* Sample invoice */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Sample invoice</CardTitle>
            </CardHeader>
            <CardContent>
              {pdfUrl ? (
                <div className="rounded-lg overflow-hidden bg-stone-100 dark:bg-coral-900/40" style={{ height: '600px' }}>
                  <iframe
                    src={`${pdfUrl}#toolbar=0&navpanes=0`}
                    className="w-full h-full border-none"
                    title="Sample invoice"
                  />
                </div>
              ) : (
                <div className="flex items-center gap-2 text-foreground-muted text-sm p-4">
                  <AlertCircle className="w-4 h-4" />
                  The sample invoice for this template isn't available.
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TemplateViewer;
